<script setup>
import { computed, onMounted, ref, toRefs, watch } from 'vue'
import { useRouter } from 'vue-router'
import { reportCatalog } from '../reports/catalogState.js'
import NaftaPosInstanceSelector from '../components/NaftaPosInstanceSelector.vue'
import { useI18n } from '../i18n'
import { chooseNaftaPosSource, session } from '../session'

const router = useRouter()
const { locale, t } = useI18n()
const switching = ref(false)
const { templates, favoriteIds, pendingFavoriteId, searchQuery, currentPage, loaded, refreshing } = toRefs(reportCatalog.state)
const actionError = ref('')
const error = computed(() => actionError.value || (reportCatalog.state.error ? t('catalogUnavailable')
  : reportCatalog.state.favoritesUnavailable ? t('favoritesUnavailable') : ''))
const loading = computed(() => !loaded.value && refreshing.value)
const pageSize = 8

function matchesSearch(template) {
  const search = searchQuery.value.trim().toLocaleLowerCase()
  return !search || `${template.displayName} ${template.kind}`.toLocaleLowerCase().includes(search)
}

function compareTemplates(left, right) {
  return left.displayName.localeCompare(right.displayName, locale.value)
}

const favoriteTemplates = computed(() => {
  const templatesById = new Map(templates.value.map((template) => [template.id, template]))
  return favoriteIds.value
    .map((templateId) => templatesById.get(templateId))
    .filter((template) => template && matchesSearch(template))
})

const catalogTemplates = computed(() => templates.value
  .filter((template) => !favoriteIds.value.includes(template.id) && matchesSearch(template))
  .sort(compareTemplates))

const totalPages = computed(() => Math.max(1, Math.ceil(catalogTemplates.value.length / pageSize)))
const pagedTemplates = computed(() => {
  const start = (currentPage.value - 1) * pageSize
  return catalogTemplates.value.slice(start, start + pageSize)
})

const visibleTemplateCount = computed(() => favoriteTemplates.value.length + catalogTemplates.value.length)

function isFavorite(templateId) {
  return favoriteIds.value.includes(templateId)
}

function load() {
  actionError.value = ''
  return reportCatalog.refresh()
}

async function selectStation(profileId) {
  if (!profileId || session.platformContext?.activeNaftaPosInstanceId === profileId) return
  switching.value = true
  actionError.value = ''
  try {
    await chooseNaftaPosSource(profileId)
  } catch (cause) {
    actionError.value = cause.message
  } finally {
    switching.value = false
  }
}

async function toggleFavorite(template) {
  actionError.value = ''
  try {
    await reportCatalog.toggleFavorite(template.id)
  } catch (cause) {
    actionError.value = cause.message
  }
}

function openTemplate(template) {
  router.push({ name: 'report-detail', params: { templateId: template.id } })
}

function changePage(page) {
  currentPage.value = Math.min(Math.max(page, 1), totalPages.value)
}

onMounted(load)

watch(searchQuery, () => {
  currentPage.value = 1
})

watch(totalPages, (pageCount) => {
  if (currentPage.value > pageCount) currentPage.value = pageCount
})

watch(() => reportCatalog.state.scope, load)
</script>

<template>
  <section class="reports-workspace">
    <div class="indicators-toolbar">
      <div class="workspace-heading">
        <h1>{{ t('reports') }}</h1>
      </div>
      <div class="indicator-actions">
        <NaftaPosInstanceSelector :context="session.platformContext" :disabled="switching" @select="selectStation" />
      </div>
    </div>
    <p v-if="error" class="notice error" role="alert">{{ error }}</p>
    <button v-if="reportCatalog.state.error" class="button button-secondary button-small" type="button" :disabled="refreshing" @click="load">{{ t('refresh') }}</button>

    <div v-if="loading" class="empty">{{ t('catalogInitialLoading') }}</div>
    <div v-else-if="loaded" class="reports-workspace-grid catalog-only">
      <section class="catalog-panel card" :aria-label="t('catalogTitle')">
        <div class="catalog-panel-heading">
          <div>
            <h2>{{ t('catalogTitle') }}</h2>
          </div>
          <span class="muted">{{ searchQuery ? t('filteredAvailable', { visible: visibleTemplateCount, total: templates.length }) : t('available', { count: templates.length }) }}</span>
          <button class="button button-secondary button-small" type="button" :disabled="refreshing" @click="load">{{ refreshing ? t('catalogRefreshing') : t('refresh') }}</button>
        </div>

        <label class="catalog-search">
          <span class="visually-hidden">{{ t('searchReports') }}</span>
          <input v-model="searchQuery" type="search" autocomplete="off" :placeholder="t('searchPlaceholder')">
        </label>

        <template v-if="loaded">
          <section v-if="favoriteTemplates.length" class="report-group favorites-group" :aria-label="t('favoritesTitle')">
            <div class="report-group-heading">
              <h3>{{ t('favoritesTitle') }} <span>{{ favoriteTemplates.length }}</span></h3>
            </div>
            <div class="report-list">
              <article v-for="template in favoriteTemplates" :key="template.id" class="report-row">
                <button class="report-row-open" type="button" @click="openTemplate(template)">
                  <span class="template-kind" :class="{ chart: template.kind === 'CHART' }">{{ template.kind === 'CHART' ? t('chart') : t('report') }}</span>
                  <strong>{{ template.displayName }}</strong>
                  <small>{{ t('parameters', { count: template.parameterCount }) }}</small>
                </button>
                <button class="favorite-button" type="button" :class="{ active: isFavorite(template.id) }" :disabled="Boolean(pendingFavoriteId)"
                  :aria-label="t('removeFavorite')" @click="toggleFavorite(template)">★</button>
              </article>
            </div>
          </section>

          <section class="report-group" :aria-label="t('allReports')">
            <div class="report-group-heading">
              <h3>{{ t('allReports') }}</h3>
              <span v-if="totalPages > 1" class="muted">{{ t('pageOf', { current: currentPage, total: totalPages }) }}</span>
            </div>
            <div v-if="pagedTemplates.length" class="report-list">
              <article v-for="template in pagedTemplates" :key="template.id" class="report-row">
                <button class="report-row-open" type="button" @click="openTemplate(template)">
                  <span class="template-kind" :class="{ chart: template.kind === 'CHART' }">{{ template.kind === 'CHART' ? t('chart') : t('report') }}</span>
                  <strong>{{ template.displayName }}</strong>
                  <small>{{ t('parameters', { count: template.parameterCount }) }}</small>
                </button>
                <button class="favorite-button" type="button" :class="{ active: isFavorite(template.id) }" :disabled="Boolean(pendingFavoriteId)"
                  :aria-label="isFavorite(template.id) ? t('removeFavorite') : t('addFavorite')" @click="toggleFavorite(template)">{{ isFavorite(template.id) ? '★' : '☆' }}</button>
              </article>
            </div>
            <p v-else class="empty">{{ t('noSearchResults') }}</p>
          </section>

          <nav v-if="totalPages > 1" class="catalog-pagination" :aria-label="t('pagination')">
            <button class="button button-secondary button-small" type="button" :disabled="currentPage === 1" @click="changePage(currentPage - 1)">{{ t('previousPage') }}</button>
            <button v-for="page in totalPages" :key="page" class="catalog-page" type="button" :class="{ active: page === currentPage }"
              :aria-label="t('pageNumber', { page })" :aria-current="page === currentPage ? 'page' : undefined" @click="changePage(page)">{{ page }}</button>
            <button class="button button-secondary button-small" type="button" :disabled="currentPage === totalPages" @click="changePage(currentPage + 1)">{{ t('nextPage') }}</button>
          </nav>
        </template>
      </section>
    </div>
  </section>
</template>
